import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  CoinsIcon, 
  CreditCardIcon,
  TrophyIcon,
  CarIcon,
  PalmtreeIcon,
  HomeIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  InfoIcon
} from "lucide-react";

// Game constants
const INITIAL_GAME_SPEED = 1.5;
const SPEED_INCREASE_INTERVAL = 10000; // Speed increases every 10 seconds
const SPEED_INCREASE_AMOUNT = 0.2; // How much to increase speed by
const MAX_GAME_SPEED = 4; // Maximum speed cap
const LANE_WIDTH = 200;
const TOTAL_LANES = 3;
const PLAYER_WIDTH = 40;
const PLAYER_HEIGHT = 40;
const GAME_HEIGHT = 400;
const GAME_WIDTH = LANE_WIDTH * TOTAL_LANES;

interface GameObject {
  x: number;
  y: number;
  lane: number;
  width: number;
  height: number;
}

interface Player extends GameObject {
  goal: string;
}

interface Goal {
  name: string;
  icon: React.ReactNode;
  requiredScore: number;
}

const GOALS: Goal[] = [
  { name: "Vacation", icon: <PalmtreeIcon className="w-8 h-8" />, requiredScore: 1000 },
  { name: "Car", icon: <CarIcon className="w-8 h-8" />, requiredScore: 2000 },
  { name: "House", icon: <HomeIcon className="w-8 h-8" />, requiredScore: 5000 }
];

export const BudgetRunnerGame = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showTutorial, setShowTutorial] = useState(true);
  const [currentGoal, setCurrentGoal] = useState<Goal>(GOALS[0]);
  const [gameSpeed, setGameSpeed] = useState(INITIAL_GAME_SPEED);
  
  const [player, setPlayer] = useState<Player>({
    x: LANE_WIDTH / 2,
    y: GAME_HEIGHT - 100,
    lane: 1,
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
    goal: GOALS[0].name
  });

  const [obstacles, setObstacles] = useState<GameObject[]>([]);
  const [coins, setCoins] = useState<GameObject[]>([]);

  // Speed progression
  useEffect(() => {
    if (!gameStarted) return;

    const speedInterval = setInterval(() => {
      setGameSpeed(prevSpeed => {
        const newSpeed = prevSpeed + SPEED_INCREASE_AMOUNT;
        if (newSpeed <= MAX_GAME_SPEED) {
          console.log(`Speed increased to: ${newSpeed}`);
          return newSpeed;
        }
        return prevSpeed;
      });
    }, SPEED_INCREASE_INTERVAL);

    return () => clearInterval(speedInterval);
  }, [gameStarted]);

  // Handle lane changes
  const changeLane = useCallback((direction: 'left' | 'right') => {
    if (!gameStarted) return;
    
    setPlayer(prev => {
      const newLane = direction === 'left' 
        ? Math.max(0, prev.lane - 1)
        : Math.min(TOTAL_LANES - 1, prev.lane + 1);
      
      return {
        ...prev,
        lane: newLane,
        x: newLane * LANE_WIDTH + (LANE_WIDTH - PLAYER_WIDTH) / 2
      };
    });
  }, [gameStarted]);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        changeLane('left');
      } else if (e.key === "ArrowRight") {
        changeLane('right');
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [changeLane]);

  // Game loop
  useEffect(() => {
    if (!gameStarted) return;

    const gameLoop = setInterval(() => {
      // Move and check obstacles
      setObstacles(prevObstacles => {
        return prevObstacles
          .map(obstacle => ({
            ...obstacle,
            y: obstacle.y + gameSpeed // Now using dynamic gameSpeed
          }))
          .filter(obstacle => {
            if (
              obstacle.lane === player.lane &&
              Math.abs(obstacle.y - player.y) < PLAYER_HEIGHT &&
              Math.abs(obstacle.x - player.x) < PLAYER_WIDTH
            ) {
              setGameOver(true);
              setGameStarted(false);
              if (score > highScore) {
                setHighScore(score);
              }
              toast.error("Game Over! You hit a high-interest loan!");
              return false;
            }
            return obstacle.y < GAME_HEIGHT + obstacle.height;
          });
      });

      // Move and check coins
      setCoins(prevCoins => {
        return prevCoins
          .map(coin => ({
            ...coin,
            y: coin.y + gameSpeed // Now using dynamic gameSpeed
          }))
          .filter(coin => {
            if (
              coin.lane === player.lane &&
              Math.abs(coin.y - player.y) < PLAYER_HEIGHT &&
              Math.abs(coin.x - player.x) < PLAYER_WIDTH
            ) {
              setScore(s => {
                const newScore = s + 10;
                if (newScore >= currentGoal.requiredScore) {
                  const nextGoalIndex = GOALS.findIndex(g => g.name === currentGoal.name) + 1;
                  if (nextGoalIndex < GOALS.length) {
                    setCurrentGoal(GOALS[nextGoalIndex]);
                    toast.success(`Congratulations! You've reached your ${currentGoal.name} goal!`);
                  } else {
                    toast.success("You've achieved all financial goals! Amazing!");
                  }
                }
                return newScore;
              });
              return false;
            }
            return coin.y < GAME_HEIGHT + coin.height;
          });
      });
    }, 1000 / 60);

    return () => clearInterval(gameLoop);
  }, [gameStarted, player.lane, player.x, player.y, score, highScore, currentGoal, gameSpeed]);

  useEffect(() => {
    if (!gameStarted) return;

    const spawnObstacle = setInterval(() => {
      const randomLane = Math.floor(Math.random() * TOTAL_LANES);
      setObstacles(prev => [
        ...prev,
        {
          x: randomLane * LANE_WIDTH + (LANE_WIDTH - 30) / 2,
          y: -50, // Start above the game area
          lane: randomLane,
          width: 30,
          height: 50
        }
      ]);
    }, 2000);

    return () => clearInterval(spawnObstacle);
  }, [gameStarted]);

  // Spawn coins
  useEffect(() => {
    if (!gameStarted) return;

    const spawnCoin = setInterval(() => {
      const randomLane = Math.floor(Math.random() * TOTAL_LANES);
      setCoins(prev => [
        ...prev,
        {
          x: randomLane * LANE_WIDTH + (LANE_WIDTH - 20) / 2,
          y: -20, // Start above the game area
          lane: randomLane,
          width: 20,
          height: 20
        }
      ]);
    }, 1500);

    return () => clearInterval(spawnCoin);
  }, [gameStarted]);

  const startGame = () => {
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setGameSpeed(INITIAL_GAME_SPEED);
    setObstacles([]);
    setCoins([]);
    setPlayer({
      x: LANE_WIDTH + (LANE_WIDTH - PLAYER_WIDTH) / 2,
      y: GAME_HEIGHT - 100,
      lane: 1,
      width: PLAYER_WIDTH,
      height: PLAYER_HEIGHT,
      goal: currentGoal.name
    });
    setShowTutorial(false);
  };

  return (
    <Card className="w-full max-w-[800px] mx-auto">
      {/* Tutorial Modal */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="sm:max-w-[425px] bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-primary/20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <InfoIcon className="w-6 h-6 text-primary animate-bounce-small" />
              Welcome to Budget Runner!
            </DialogTitle>
            <DialogDescription className="space-y-4 pt-4">
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in">
                {currentGoal.icon}
                <p>Run towards your financial goal: {currentGoal.name}!</p>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in delay-100">
                <CoinsIcon className="w-8 h-8 text-yellow-500 animate-spin-slow" />
                <p>Collect coins to reach your savings goal!</p>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in delay-200">
                <CreditCardIcon className="w-8 h-8 text-red-500" />
                <p>Dodge high-interest loans and unnecessary expenses!</p>
              </div>

              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in delay-300">
                <div className="flex gap-2">
                  <ArrowLeftIcon className="w-6 h-6" />
                  <ArrowRightIcon className="w-6 h-6" />
                </div>
                <p>Use arrow keys to switch lanes</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={startGame}
              size="lg"
              className="w-full sm:w-auto animate-pulse bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
            >
              Start Running!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div 
        className="relative bg-gradient-to-b from-blue-200 via-blue-100 to-green-200 rounded-lg overflow-hidden shadow-xl"
        style={{ height: `${GAME_HEIGHT}px` }}
      >
        {/* Game canvas */}
        <div className="absolute inset-0">
          {/* Lane markers */}
          <div className="absolute inset-0 z-0">
            {Array.from({ length: TOTAL_LANES + 1 }).map((_, index) => (
              <div 
                key={index}
                className="absolute h-full w-px bg-background/20"
                style={{ left: `${index * LANE_WIDTH}px` }}
              />
            ))}
          </div>

          {/* Ground */}
          <div className="absolute bottom-0 w-full z-0">
            <div className="h-20 bg-gradient-to-b from-green-300 to-green-400" />
            <div className="h-4 bg-gradient-to-b from-brown-400 to-brown-500" />
          </div>

          {/* Player */}
          <div 
            className="absolute z-10 transition-all duration-200"
            style={{
              left: `${player.x}px`,
              top: `${player.y}px`,
              width: `${player.width}px`,
              height: `${player.height}px`
            }}
          >
            {currentGoal.icon}
          </div>

          {/* Obstacles */}
          {obstacles.map((obstacle, index) => (
            <div
              key={index}
              className="absolute z-20"
              style={{
                left: `${obstacle.x}px`,
                top: `${obstacle.y}px`,
                width: `${obstacle.width}px`,
                height: `${obstacle.height}px`
              }}
            >
              <CreditCardIcon className="w-full h-full text-red-500 drop-shadow-md" />
            </div>
          ))}

          {/* Coins */}
          {coins.map((coin, index) => (
            <div
              key={index}
              className="absolute z-30"
              style={{
                left: `${coin.x}px`,
                top: `${coin.y}px`,
                width: `${coin.width}px`,
                height: `${coin.height}px`
              }}
            >
              <CoinsIcon className="w-full h-full text-yellow-500 drop-shadow-lg animate-spin-slow" />
            </div>
          ))}
        </div>

        {/* Game overlay */}
        {!gameStarted && !showTutorial && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center text-white z-50">
            <h2 className="text-4xl font-bold mb-4 flex items-center gap-2">
              {gameOver ? (
                <>
                  <TrophyIcon className="w-8 h-8 text-yellow-400 animate-bounce-small" />
                  Game Over!
                </>
              ) : (
                "Budget Runner"
              )}
            </h2>
            {gameOver && (
              <div className="text-center mb-4 space-y-2">
                <p className="text-xl flex items-center justify-center gap-2">
                  <CoinsIcon className="w-6 h-6 text-yellow-500" />
                  Score: {score}
                </p>
                <p className="text-xl flex items-center justify-center gap-2">
                  <TrophyIcon className="w-6 h-6 text-yellow-500" />
                  High Score: {highScore}
                </p>
                <p className="text-lg mt-2 flex items-center justify-center gap-2">
                  {currentGoal.icon}
                  Goal: {currentGoal.name}
                </p>
              </div>
            )}
            <Button 
              size="lg"
              onClick={startGame}
              className="animate-bounce-small bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
            >
              {gameOver ? "Try Again" : "Start Game"}
            </Button>
          </div>
        )}

        {/* Score and goal display */}
        {gameStarted && (
          <div className="absolute top-4 left-4 space-y-2 z-40">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg p-2 flex items-center gap-2 shadow-lg animate-fade-in">
              <CoinsIcon className="w-6 h-6 text-yellow-500 animate-spin-slow" />
              <span className="font-bold text-lg">{score}</span>
            </div>
            <div className="bg-background/90 backdrop-blur-sm rounded-lg p-2 shadow-lg animate-fade-in delay-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium flex items-center gap-1">
                  {currentGoal.icon}
                  {currentGoal.name}
                </span>
                <span className="text-sm text-muted-foreground">
                  {score}/{currentGoal.requiredScore}
                </span>
              </div>
              <Progress value={(score / currentGoal.requiredScore) * 100} className="h-2" />
            </div>
          </div>
        )}

        {/* Mobile controls - Updated for better touch experience */}
        {gameStarted && (
          <div className="absolute bottom-8 left-0 right-0 z-40 flex justify-center gap-8 md:hidden">
            <Button
              variant="secondary"
              size="lg"
              className="h-20 w-20 rounded-full bg-background/90 hover:bg-background shadow-lg active:scale-95 transition-all duration-200 backdrop-blur-sm"
              onClick={() => changeLane('left')}
            >
              <ArrowLeftIcon className="w-10 h-10 text-primary" />
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="h-20 w-20 rounded-full bg-background/90 hover:bg-background shadow-lg active:scale-95 transition-all duration-200 backdrop-blur-sm"
              onClick={() => changeLane('right')}
            >
              <ArrowRightIcon className="w-10 h-10 text-primary" />
            </Button>
          </div>
        )}
      </div>

      {/* Game instructions - Updated for mobile */}
      <div className="mt-4 text-center text-sm text-muted-foreground px-4">
        <p className="hidden md:block">Use left and right arrow keys to switch lanes.</p>
        <p className="md:hidden">Tap the arrow buttons to move left and right.</p>
        <p>Collect coins and avoid credit cards!</p>
      </div>
    </Card>
  );
};
